import { useState, useRef, useCallback, useEffect, useMemo, lazy, Suspense } from "react";
import {
  FileText,
  User,
  MapPin,
  Package,
  Calendar,
  ScrollText,
  X,
  MessageSquare,
  Layers,
  BookOpen,
  LayoutGrid,
  Images,
  Lightbulb,
  Plus,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/client/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/client/components/ui/alert-dialog";
import { Button } from "@/client/components/ui/button";
import { cn } from "@/client/lib/utils";
import { selectBoardCard } from "@/client/hooks/use-board-state";
import { useCreateChapter } from "@/client/hooks/use-chapters";
import { useChats } from "@/client/hooks/use-chats";
import { useCodex } from "@/client/hooks/use-codex";
import { useStory } from "@/client/hooks/use-stories";
import { readDraft } from "@/client/db/drafts";
import { sessionUser } from "@/client/db";
import { useStoryData } from "@/client/hooks/use-story-data";
import { useTabActions, useTabs, type Tab } from "@/client/hooks/use-tabs-context";
import type { CodexType } from "@/client/hooks/use-codex";

// Each kind of tab loads when one is first shown
const WritingArea = lazy(() => import("@/client/components/editor/writing-area").then((m) => ({ default: m.WritingArea })));
const CodexEditorTab = lazy(() => import("@/client/components/codex/codex-editor-tab").then((m) => ({ default: m.CodexEditorTab })));
const ChatTab = lazy(() => import("@/client/components/chat/chat-tab").then((m) => ({ default: m.ChatTab })));
const StoryEditorTab = lazy(() => import("@/client/components/editor/story-editor-tab").then((m) => ({ default: m.StoryEditorTab })));
const PartEditorTab = lazy(() => import("@/client/components/editor/part-editor-tab").then((m) => ({ default: m.PartEditorTab })));
const ChapterEditorTab = lazy(() => import("@/client/components/editor/chapter-editor-tab").then((m) => ({ default: m.ChapterEditorTab })));
const StoryBoardTab = lazy(() => import("@/client/components/board/story-board-tab").then((m) => ({ default: m.StoryBoardTab })));
const ArtTab = lazy(() => import("@/client/components/art/art-tab").then((m) => ({ default: m.ArtTab })));
const SuggestionReview = lazy(() => import("@/client/components/agents/suggestion-review").then((m) => ({ default: m.SuggestionReview })));

interface TabbedContentProps {
  storyId: string;
  onShowInbox: () => void;
  /** Opens the box for jotting down an idea */
  onCapture: () => void;
}

// Icon mapping for codex types
const CODEX_ICONS: Record<CodexType, typeof User> = {
  character: User,
  location: MapPin,
  object: Package,
  event: Calendar,
  lore: ScrollText,
};

// Get icon for a tab
function getTabIcon(tab: Tab) {
  if (tab.type === "scene") {
    return FileText;
  }
  if (tab.type === "codex" && tab.codexType) {
    return CODEX_ICONS[tab.codexType] || ScrollText;
  }
  if (tab.type === "chat") {
    return MessageSquare;
  }
  if (tab.type === "part") {
    return Layers;
  }
  if (tab.type === "chapter") {
    return BookOpen;
  }
  if (tab.type === "story") {
    return BookOpen;
  }
  if (tab.type === "board") {
    return LayoutGrid;
  }
  if (tab.type === "art") {
    return Images;
  }
  return FileText;
}

// Sortable tab component
interface SortableTabProps {
  tab: Tab;
  /** From the live data, so renames anywhere show */
  title: string;
  isActive: boolean;
  isDirty: boolean;
  onClose: (e: React.SyntheticEvent, tabId: string) => void;
}

function SortableTab({ tab, title, isActive, isDirty, onClose }: SortableTabProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id, attributes: { role: "tab" } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const Icon = getTabIcon(tab);

  return (
    <TabsTrigger
      ref={setNodeRef}
      style={style}
      value={tab.id}
      data-tab-id={tab.id}
      className={cn(
        "relative rounded-none border-b-2 border-b-transparent data-[state=active]:border-b-primary data-[state=active]:bg-background h-full px-3 gap-1.5 shrink-0 flex-initial cursor-grab active:cursor-grabbing md:touch-none",
        isDragging && "opacity-50 z-50 shadow-lg"
      )}
      {...attributes}
      {...listeners}
      aria-keyshortcuts="Delete"
      onKeyDown={(e) => {
        if (e.key === "Delete") onClose(e, tab.id);
        else listeners?.onKeyDown?.(e);
      }}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span data-region="tab-title" className="max-w-[120px] truncate">{title}</span>
      {isDirty && (
        <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
      )}
      {/* A span, not a button: the tab itself is a button, and buttons can't
          nest. A tab's contents are presentational to screen readers, so
          keyboard and screen reader users close the focused tab with Delete. */}
      <span
        aria-hidden
        data-tab-close
        onClick={(e) => onClose(e, tab.id)}
        onPointerDown={(e) => e.stopPropagation()}
        className="ml-1 p-0.5 rounded hover:bg-muted-foreground/20 opacity-60 hover:opacity-100"
        title={`Close ${title}`}
      >
        <X className="h-3 w-3" />
      </span>
    </TabsTrigger>
  );
}

// Tab overlay for drag preview
interface TabOverlayProps {
  tab: Tab;
  title: string;
  isDirty: boolean;
}

function TabOverlay({ tab, title, isDirty }: TabOverlayProps) {
  const Icon = getTabIcon(tab);

  return (
    <div className="flex items-center gap-1.5 px-3 h-10 bg-background border rounded-md shadow-lg cursor-grabbing">
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span data-region="tab-title" className="max-w-[120px] truncate">{title}</span>
      {isDirty && (
        <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
      )}
      <div className="ml-1 p-0.5">
        <X className="h-3 w-3 opacity-60" />
      </div>
    </div>
  );
}

export function TabbedContent({ storyId, onShowInbox, onCapture }: TabbedContentProps) {
  const {
    tabs,
    activeTabId,
    setActiveTab,
    closeTab,
    reorderTabs,
    isTabDirty,
    markTabDirty,
    markTabClean,
    updateTabTitle,
  } = useTabs();

  const titleOf = useTabTitles(storyId);
  useCloseGoneTabs(storyId);
  const scroll = useTabScroll(activeTabId);

  // State for unsaved changes confirmation dialog
  const [pendingCloseTabId, setPendingCloseTabId] = useState<string | null>(null);
  // State for drag overlay
  const [activeId, setActiveId] = useState<string | null>(null);

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before starting drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      reorderTabs(active.id as string, over.id as string);
    }
  };

  // Handle tab close - check for unsaved changes
  const handleTabClose = (e: React.SyntheticEvent, tabId: string) => {
    e.stopPropagation();
    if (isTabDirty(tabId)) {
      // Show confirmation dialog
      setPendingCloseTabId(tabId);
    } else {
      // Close immediately
      closeTab(tabId, true);
    }
  };

  // Confirm close (discard changes)
  const handleConfirmClose = () => {
    if (pendingCloseTabId) {
      closeTab(pendingCloseTabId, true);
      setPendingCloseTabId(null);
    }
  };

  // Cancel close
  const handleCancelClose = () => {
    setPendingCloseTabId(null);
  };

  // Get the active tab for drag overlay
  const activeDragTab = activeId ? tabs.find((t) => t.id === activeId) : null;

  // Ref for the tabs list container to enable horizontal scrolling with mouse wheel
  const tabsListRef = useRef<HTMLDivElement | null>(null);

  // Handle wheel event to scroll horizontally with smooth animation
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (tabsListRef.current) {
      // Prevent default vertical scroll
      e.preventDefault();
      // Scroll horizontally with smooth behavior and 3x multiplier for faster scrolling
      tabsListRef.current.scrollBy({
        left: e.deltaY * 3,
        behavior: "smooth",
      });
    }
  }, []);

  // Scroll the active tab into view when it changes
  // Use requestAnimationFrame to ensure DOM is updated before scrolling
  useEffect(() => {
    if (!activeTabId || !tabsListRef.current) return;

    // Delay to ensure the tab element is rendered in the DOM
    requestAnimationFrame(() => {
      // Find the tab element by data attribute
      const tabElement = tabsListRef.current?.querySelector(
        `[data-tab-id="${activeTabId}"]`
      ) as HTMLElement | null;

      if (tabElement) {
        // Use scrollIntoView with smooth behavior and center alignment
        tabElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    });
  }, [activeTabId]);

  // Empty state when no tabs are open
  if (tabs.length === 0) {
    return <NothingOpen storyId={storyId} onCapture={onCapture} />;
  }

  return (
    <Tabs
      value={activeTabId || undefined}
      onValueChange={setActiveTab}
      className="h-full flex flex-col gap-0"
    >
      {/* Tab list with drag and drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={tabs.map((t) => t.id)}
          strategy={horizontalListSortingStrategy}
        >
          <div
            ref={tabsListRef}
            onWheel={handleWheel}
            className="w-full overflow-x-auto scrollbar-none"
          >
            <TabsList data-region="tab-bar" className="w-full justify-start rounded-none border-b bg-muted/30 h-10 p-0">
              {tabs.map((tab) => (
                <SortableTab
                  key={tab.id}
                  tab={tab}
                  title={titleOf(tab)}
                  isActive={tab.id === activeTabId}
                  isDirty={isTabDirty(tab.id)}
                  onClose={handleTabClose}
                />
              ))}
            </TabsList>
          </div>
        </SortableContext>

        {/* Drag overlay - shows a preview while dragging */}
        <DragOverlay>
          {activeDragTab ? (
            <TabOverlay tab={activeDragTab} title={titleOf(activeDragTab)} isDirty={isTabDirty(activeDragTab.id)} />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Tab content panels. Only the active one is mounted; where each was
          scrolled to comes back with it. */}
      {tabs.map((tab) => (
        <TabsContent
          key={tab.id}
          value={tab.id}
          ref={tab.id === activeTabId ? scroll.panelRef : undefined}
          onScrollCapture={scroll.onScroll}
          className="flex-1 min-h-0 mt-0 overflow-hidden"
        >
          <Suspense fallback={null}>
          {tab.type === "scene" ? (
            <WritingArea
              storyId={storyId}
              sceneId={tab.entityId}
              onSceneChange={() => {
                // No-op - scene changes handled via tabs
              }}
            />
          ) : tab.type === "codex" ? (
            <CodexEditorTab
              tabId={tab.id}
              storyId={storyId}
              entryId={tab.entityId}
              onDirtyChange={(dirty: boolean) => {
                if (dirty) {
                  markTabDirty(tab.id);
                } else {
                  markTabClean(tab.id);
                }
              }}
              onTitleChange={(title: string) => {
                updateTabTitle(tab.id, title);
              }}
            />
          ) : tab.type === "proposal" ? (
            <SuggestionReview storyId={storyId} proposalId={tab.entityId} />
          ) : tab.type === "chat" ? (
            <ChatTab
              chatId={tab.entityId}
              storyId={storyId}
              onTitleChange={(title: string) => {
                updateTabTitle(tab.id, title);
              }}
            />
          ) : tab.type === "part" ? (
            <PartEditorTab
              tabId={tab.id}
              storyId={storyId}
              partId={tab.entityId}
              onDirtyChange={(dirty: boolean) => {
                if (dirty) {
                  markTabDirty(tab.id);
                } else {
                  markTabClean(tab.id);
                }
              }}
              onTitleChange={(title: string) => {
                updateTabTitle(tab.id, title);
              }}
            />
          ) : tab.type === "chapter" ? (
            <ChapterEditorTab
              tabId={tab.id}
              storyId={storyId}
              chapterId={tab.entityId}
              onDirtyChange={(dirty: boolean) => {
                if (dirty) {
                  markTabDirty(tab.id);
                } else {
                  markTabClean(tab.id);
                }
              }}
              onTitleChange={(title: string) => {
                updateTabTitle(tab.id, title);
              }}
            />
          ) : tab.type === "story" ? (
            <StoryEditorTab
              tabId={tab.id}
              storyId={storyId}
              onDirtyChange={(dirty: boolean) => {
                if (dirty) {
                  markTabDirty(tab.id);
                } else {
                  markTabClean(tab.id);
                }
              }}
              onTitleChange={(title: string) => {
                updateTabTitle(tab.id, title);
              }}
            />
          ) : tab.type === "board" ? (
            <StoryBoardTab storyId={storyId} onShowInbox={onShowInbox} />
          ) : tab.type === "art" ? (
            <ArtTab storyId={storyId} />
          ) : null}
          </Suspense>
        </TabsContent>
      ))}

      {/* Unsaved changes confirmation dialog */}
      <AlertDialog open={pendingCloseTabId !== null} onOpenChange={(open) => !open && handleCancelClose()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              This tab has unsaved changes. Are you sure you want to close it?
              Your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose}>
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Tabs>
  );
}

/** With every tab closed: the ways back into the story, as buttons */
function NothingOpen({ storyId, onCapture }: { storyId: string; onCapture: () => void }) {
  const { openBoardTab } = useTabActions();
  const { outline } = useStoryData();
  const createChapter = useCreateChapter();

  const addFirstChapter = async () => {
    try {
      const chapter = await createChapter.mutateAsync({ storyId, data: { title: "Chapter 1", partId: null } });
      openBoardTab(storyId);
      selectBoardCard(storyId, `chapter:${chapter.id}`);
    } catch {
      // Reported by the collection
    }
  };

  return (
    <div className="h-full flex flex-col items-center justify-center text-center p-8">
      <FileText className="h-16 w-16 text-muted-foreground/30 mb-4" />
      <h2 className="text-xl font-semibold text-muted-foreground mb-2">Nothing open</h2>
      <p className="text-muted-foreground max-w-md mb-6">
        Pick a scene in Structure, or start from one of these.
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        <Button onClick={() => openBoardTab(storyId)}>
          <LayoutGrid />
          Open the story board
        </Button>
        <Button variant="outline" onClick={onCapture}>
          <Lightbulb />
          Jot down an idea
        </Button>
        {outline.chapters.length === 0 && !outline.isLoading && (
          <Button variant="outline" onClick={() => void addFirstChapter()} disabled={createChapter.isPending}>
            <Plus />
            Add a first chapter
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Each tab's title from the live data, so a rename anywhere, by anyone, or
 * undone, shows at once. The title saved with the tab is the fallback.
 */
function useTabTitles(storyId: string): (tab: Tab) => string {
  const { outline } = useStoryData();
  const { data: story } = useStory(storyId);
  const { data: codex } = useCodex(storyId);
  const { data: chats } = useChats(storyId);
  const codexById = useMemo(() => new Map(codex.map((entry) => [entry.id, entry.name])), [codex]);
  const chatById = useMemo(() => new Map(chats.map((chat) => [chat.id, chat.title])), [chats]);
  return (tab) => {
    const live =
      tab.type === "scene"
        ? outline.sceneById.get(tab.entityId)?.title
        : tab.type === "chapter"
          ? outline.chapterById.get(tab.entityId)?.title
          : tab.type === "part"
            ? outline.parts.find((part) => part.id === tab.entityId)?.title
            : tab.type === "story"
              ? story?.name
              : tab.type === "codex"
                ? codexById.get(tab.entityId)
                : tab.type === "chat"
                  ? chatById.get(tab.entityId)
                  : tab.type === "board"
                    ? "Story board"
                    : tab.type === "art"
                      ? "Art"
                      : undefined;
    return live || tab.title || "Untitled";
  };
}

/**
 * Closes tabs whose part, chapter, scene, or codex entry was deleted, here or
 * by someone else, rather than leaving a "not found" tab. A scene with
 * writing kept on this device stays open, so it can still be downloaded.
 */
function useCloseGoneTabs(storyId: string) {
  const { tabs, closeTab, isTabDirty } = useTabs();
  const { outline } = useStoryData();
  const codex = useCodex(storyId);
  useEffect(() => {
    if (outline.isLoading) return;
    const codexIds = new Set(codex.data.map((entry) => entry.id));
    for (const tab of tabs) {
      const gone =
        tab.type === "scene"
          ? !outline.sceneById.has(tab.entityId) && !readDraft(sessionUser().id, storyId, tab.entityId)
          : tab.type === "chapter"
            ? !outline.chapterById.has(tab.entityId)
            : tab.type === "part"
              ? !outline.parts.some((part) => part.id === tab.entityId)
              : tab.type === "codex"
                ? codex.isReady && !codexIds.has(tab.entityId)
                : false;
      if (gone && !isTabDirty(tab.id)) closeTab(tab.id);
    }
  }, [tabs, outline, codex.data, codex.isReady, storyId, closeTab, isTabDirty]);
}

/** A scrolled element's place under the tab's panel, as child indexes */
function pathTo(panel: Element, element: Element): number[] | null {
  const path: number[] = [];
  for (let node: Element | null = element; node && node !== panel; node = node.parentElement) {
    const parent: Element | null = node.parentElement;
    if (!parent) return null;
    path.unshift(Array.prototype.indexOf.call(parent.children, node));
  }
  return path;
}

function atPath(panel: Element, path: number[]): Element | null {
  let node: Element | null = panel;
  for (const index of path) node = node?.children[index] ?? null;
  return node;
}

type ScrollSpots = Map<string, { path: number[]; top: number; left: number }>;

/**
 * Remembers where each tab was scrolled, and scrolls a tab back there when
 * it's shown again. Tabs unmount when hidden, so their content may take a
 * moment to load; restoring keeps trying for a little while.
 */
function useTabScroll(activeTabId: string | null) {
  const spots = useRef(new Map<string, ScrollSpots>());
  const panel = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef(activeTabId);
  activeRef.current = activeTabId;
  const restoring = useRef(false);

  const onScroll = useCallback((event: React.UIEvent) => {
    const tabId = activeRef.current;
    const target = event.target as Element;
    if (!tabId || !panel.current || restoring.current || !panel.current.contains(target)) return;
    const path = pathTo(panel.current, target);
    if (!path) return;
    const forTab = spots.current.get(tabId) ?? new Map();
    forTab.set(path.join("."), { path, top: target.scrollTop, left: target.scrollLeft });
    spots.current.set(tabId, forTab);
  }, []);

  useEffect(() => {
    const forTab = activeTabId ? spots.current.get(activeTabId) : undefined;
    if (!forTab || forTab.size === 0) return;
    restoring.current = true;
    const pending = new Map(forTab);
    let frame = 0;
    const started = performance.now();
    const tick = () => {
      for (const [key, spot] of pending) {
        const element = panel.current ? atPath(panel.current, spot.path) : null;
        if (!element) continue;
        element.scrollTo({ top: spot.top, left: spot.left });
        // Done once it got there; content still loading may not be tall enough yet
        if (Math.abs(element.scrollTop - spot.top) < 2 && Math.abs(element.scrollLeft - spot.left) < 2) pending.delete(key);
      }
      if (pending.size > 0 && performance.now() - started < 1500) frame = requestAnimationFrame(tick);
      else restoring.current = false;
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      restoring.current = false;
    };
  }, [activeTabId]);

  return { panelRef: panel, onScroll };
}
